import { Typography } from "@material-ui/core"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCheck } from "@fortawesome/free-solid-svg-icons"

import {
  ChecklistResponseItem,
  HydratedChecklistEntry,
  SelectedSection,
  keyToIconDef,
} from "../../../utils/safetyPlanUtils"
import { colors } from "../../../utils/theme"

interface KeepingMyselfSafeChecklistProps {
  selected: SelectedSection
  hydratedChecklist: HydratedChecklistEntry[]
}

export default function KeepingMyselfSafeChecklist({
  selected,
  hydratedChecklist,
}: KeepingMyselfSafeChecklistProps) {
  // Ignores the categoryId key for now. If we ever introduce more than one checklist, we should account for that
  const allItems: ChecklistResponseItem[] = hydratedChecklist.flatMap(
    (entry) => entry.items
  )
  const completedCount = allItems.filter((item) => item.completed).length
  const totalCount = allItems.length
  const progressPct = totalCount > 0 ? (completedCount / totalCount) * 100 : 0

  return (
    <div style={{ marginBottom: 20, borderRadius: 8, overflow: "hidden" }}>
      <div
        style={{
          display: "flex",
          padding: "12px 20px",
          alignItems: "center",
          gap: 10,
          borderBottom: `1px solid ${colors.safetyPlan.grayscale.black10}`,
          background: colors.neutral.white,
        }}
      >
        <div
          style={{
            display: "flex",
            width: 36,
            height: 36,
            minWidth: 36,
            minHeight: 36,
            justifyContent: "center",
            alignItems: "center",
            borderRadius: "50%",
            background: selected.icon_color,
            color: "white",
            flexShrink: 0,
          }}
        >
          <FontAwesomeIcon icon={keyToIconDef(selected.icon)} />
        </div>
        <Typography
          variant="h5"
          component="div"
          style={{ color: colors.safetyPlan.ink, lineHeight: "20px" }}
        >
          {selected.header}
        </Typography>
        <div
          style={{
            display: "flex",
            padding: 12,
            justifyContent: "center",
            alignItems: "center",
            borderRadius: "50%",
            background: colors.safetyPlan.lime,
            color: "white",
            marginLeft: "auto",
          }}
        >
          <FontAwesomeIcon icon={faCheck} />
        </div>
      </div>

      {/* Checklist body */}
      <div
        style={{
          display: "flex",
          padding: "16px 20px 24px",
          flexDirection: "column",
          gap: 12,
          background: colors.safetyPlan.cardSecondaryBackground,
        }}
      >
        <Typography
          variant="h5"
          component="div"
          style={{ textAlign: "center", color: colors.safetyPlan.ink }}
        >
          Keep Me Safe Checklist
        </Typography>
        <Typography
          component="div"
          style={{
            textAlign: "center",
            fontSize: 14,
            color: colors.safetyPlan.midnight,
          }}
        >
          Check items off as you complete them
        </Typography>

        <div
          style={{
            height: 6,
            background: colors.safetyPlan.grayscale.black10,
            borderRadius: 3,
          }}
        >
          <div
            style={{
              width: `${progressPct}%`,
              height: "100%",
              background: colors.safetyPlan.lime,
              borderRadius: 3,
            }}
          />
        </div>

        <Typography
          component="div"
          style={{
            textAlign: "right",
            fontSize: 12,
            color: colors.safetyPlan.midnight,
          }}
        >
          Completed {completedCount} of {totalCount}
        </Typography>

        {/* Checklist items */}
        {allItems.map((item) => (
          <div
            key={item.id}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "12px 16px",
              borderBottom: `1px solid ${colors.safetyPlan.grayscale.black10}`,
            }}
          >
            {/* Completed indicator */}
            <div
              style={{
                display: "flex",
                width: 28,
                height: 28,
                minWidth: 28,
                justifyContent: "center",
                alignItems: "center",
                borderRadius: "50%",
                background: item.completed
                  ? colors.safetyPlan.lime
                  : "transparent",
                border: item.completed
                  ? "none"
                  : `2px solid ${colors.safetyPlan.grayscale.black10}`,
                color: "white",
                flexShrink: 0,
              }}
            >
              {item.completed && (
                <FontAwesomeIcon icon={faCheck} style={{ fontSize: 12 }} />
              )}
            </div>
            <Typography
              component="div"
              style={{ fontSize: 16, color: colors.safetyPlan.ink }}
            >
              {item.response_text}
            </Typography>
          </div>
        ))}
      </div>
    </div>
  )
}
